import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function SEOHead() {
  useEffect(() => {
    document.title = "Need Money for Medical Treatment? | Raise Funds | DonatePe";
    const setMeta = (name, content, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("description", "Need money for medical treatment? Raise funds quickly on DonatePe. Trusted by thousands of families across India. 100% transparent, secure & fast.");
    setMeta("keywords", "need money medical treatment, raise funds medical emergency India, crowdfunding medical bills, DonatePe medical fundraiser");
    setMeta("robots", "index, follow");
    setMeta("og:title", "Need Money for Medical Treatment? | DonatePe", true);
    setMeta("og:description", "Raise funds for hospital bills, surgeries, ICU charges & more. Trusted by families across India.", true);
    setMeta("og:type", "website", true);
    setMeta("twitter:card", "summary_large_image");

    let ld = document.getElementById("ld-json-crowd");
    if (!ld) { ld = document.createElement("script"); ld.id = "ld-json-crowd"; ld.type = "application/ld+json"; document.head.appendChild(ld); }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Need Money for Medical Treatment? | DonatePe",
      description: "Raise funds for medical treatment on DonatePe. Fast, secure & trusted across India.",
      url: "https://donatePe.in/crowdfunding",
      provider: { "@type": "Organization", name: "DonatePe", url: "https://donatePe.in" },
    });
  }, []);
  return null;
}

const CheckIcon = () => (
  <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={3} viewBox="0 0 24 24">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export default function CrowdfundingPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  // Check for logged in user
  useEffect(() => {
    const storedUser = sessionStorage.getItem("user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (err) {
        setUser(null);
      }
    }
  }, []);

  // Purpose options for dropdown
  const purposeOptions = [
    {
      value: "medical",
      label: "Medical",
      icon: "fa-heart-pulse",
      color: "from-rose-500 to-red-500",
    },
    {
      value: "education",
      label: "Education",
      icon: "fa-graduation-cap",
      color: "from-blue-500 to-indigo-500",
    },
    {
      value: "emergency",
      label: "Emergency",
      icon: "fa-bolt",
      color: "from-amber-500 to-orange-500",
    },
  ];

  // Handle Start Fundraiser click based on user login status
  const handleStartFundraiser = () => {
    if (user) {
      // Logged in user - go to detailsScreen
      navigate("/detailsScreen", {
        state: {
          userId: user.id || user.userId
        }
      });
    } else {
      // Non-logged in user - go to start-fundraiser
      localStorage.removeItem("preselectedPurpose");
      navigate("/start-fundraiser");
    }
  };

  // Handle Get a Callback - opens contact form or shows alert
  const handleGetCallback = () => {
    // You can implement this to open a contact form modal or navigate to contact page
    navigate("/contact");
  };

  return (
    <>
      <SEOHead />
      <div className="min-h-screen bg-white" style={{ fontFamily: "DM Sans, sans-serif" }}>
        <link href="https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

        {/* ── SECTION 1: Hero — Need Money ── */}
        <section
          className="relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #E0F8F7 0%, #F0FDFC 55%, #ffffff 100%)" }}
          aria-labelledby="hero-heading"
        >
          <div className="absolute top-[-100px] right-[-60px] w-80 h-80 rounded-full opacity-50 pointer-events-none"
            style={{ background: "radial-gradient(circle, #B2EEEC 0%, transparent 70%)" }} />
          <div className="absolute bottom-[-80px] left-[-40px] w-64 h-64 rounded-full opacity-40 pointer-events-none"
            style={{ background: "radial-gradient(circle, #B2EEEC 0%, transparent 70%)" }} />

          <div className="max-w-6xl mx-auto px-5 py-16 md:py-24 relative z-10">
            <div className="grid md:grid-cols-2 gap-12 items-center">

              {/* Left Text */}
              <div>
                <div
                  className="inline-flex items-center gap-2 bg-white border text-xs font-bold px-4 py-1.5 rounded-full mb-5 shadow-sm"
                  style={{ borderColor: "#B2EEEC", color: "#006B68" }}
                >
                  <span className="w-2 h-2 rounded-full inline-block" style={{ background: "#00B7B3" }} />
                  You're in the right place
                </div>
                <h1
                  className="text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight mb-4"
                  style={{ fontFamily: "Sora, sans-serif" }}
                >
                  Need Money for Your{" "}
                  <span style={{ color: "#00B7B3" }}>Medical Treatment?</span>
                </h1>
                <p className="text-gray-500 text-lg leading-relaxed mb-6">
                  Don't panic — you're in the right place. Thousands of families have already raised funds successfully for medical emergencies. You can too.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleStartFundraiser}
                    className="text-white font-bold px-7 py-3.5 rounded-full text-base transition-all hover:-translate-y-0.5 transform"
                    style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif", boxShadow: "0 8px 24px rgba(0,183,179,0.28)" }}
                  >
                    Start a Free Fundraiser
                  </button>
                  <button
                    onClick={handleGetCallback}
                    className="bg-white font-bold border-2 px-7 py-3.5 rounded-full text-base transition-all hover:-translate-y-0.5 transform"
                    style={{ color: "#00B7B3", borderColor: "#00B7B3", fontFamily: "Sora, sans-serif" }}
                  >
                    Get a Callback
                  </button>
                </div>
              </div>

              {/* Right Image — Patient / Hospital / Emotional Support */}
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ boxShadow: "0 24px 64px rgba(0,183,179,0.15)" }}>
                  <img
                    src="assets/patient.jpg"
                    alt="Patient receiving emotional support from family in hospital"
                    className="w-full h-[400px] object-cover"
                    loading="eager"
                  />
                </div>
                <div
                  className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl px-5 py-4 border z-10"
                  style={{ borderColor: "#B2EEEC" }}
                >
                  <div className="font-extrabold text-xl" style={{ color: "#00B7B3", fontFamily: "Sora, sans-serif" }}>10,000+</div>
                  <div className="text-xs text-gray-400 mt-0.5">Families helped across India</div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 2: Raise Funds with DonatePe ── */}
        <section className="py-20 px-5" aria-labelledby="raise-heading">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-14 items-center">

              {/* Text */}
              <div>
                <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#00B7B3" }}>Why DonatePe</p>
                <h2
                  id="raise-heading"
                  className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-5"
                  style={{ fontFamily: "Sora, sans-serif" }}
                >
                  Raise Funds for Medical Treatment with DonatePe
                </h2>
                <p className="text-gray-500 text-base leading-relaxed mb-7">
                  Medical emergencies can happen anytime — but lack of money should never stop treatment. With DonatePe, you can raise funds quickly and securely for hospital bills, surgeries, medicines, ICU charges, and more.
                </p>
                <ul className="space-y-3">
                  {[
                    "Trusted by patients and families across India",
                    "Fast and secure payment system",
                    "24/7 support from our dedicated team",
                    "100% transparency in donations",
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <span className="w-6 h-6 min-w-[24px] rounded-full flex items-center justify-center" style={{ background: "#00B7B3" }}>
                        <CheckIcon />
                      </span>
                      <span className="text-gray-700 font-medium text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Image — Donation / Payment / Trust */}
              <div className="relative">
                <img
                  src="assets/donation.png"
                  alt="Secure online donation and payment system"
                  className="w-full h-[380px]"
                  loading="lazy"
                />
                <div
                  className="absolute -top-4 -right-4 text-white rounded-2xl shadow-lg px-4 py-3 text-center z-10"
                  style={{ background: "#00B7B3" }}
                >
                  <div className="font-extrabold text-lg" style={{ fontFamily: "Sora, sans-serif" }}>100%</div>
                  <div className="text-xs font-medium opacity-90">Transparent</div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 3: What is Crowdfunding ── */}
        <section className="bg-gray-50 py-20 px-5" aria-labelledby="what-heading">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-14 items-center">

              {/* Image — Crowd Helping / Community */}
              <div className="relative order-2 md:order-1">
                <div className="rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src="assets/crowdfunding.png"
                    alt="Community of people coming together to help patients in need"
                    className="w-full h-[300px] object-container"
                    loading="lazy"
                  />
                </div>
                <div
                  className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl p-5 border max-w-[180px] z-10"
                  style={{ borderColor: "#B2EEEC" }}
                >
                  <div className="font-extrabold text-2xl mb-1" style={{ color: "#00B7B3", fontFamily: "Sora, sans-serif" }}>₹0</div>
                  <div className="text-xs text-gray-500 font-medium">No repayment needed. Ever.</div>
                </div>
              </div>

              {/* Text */}
              <div className="order-1 md:order-2">
                <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#00B7B3" }}>Understanding</p>
                <h2
                  id="what-heading"
                  className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-5"
                  style={{ fontFamily: "Sora, sans-serif" }}
                >
                  What is Crowdfunding?
                </h2>
                <p className="text-gray-500 text-base leading-relaxed mb-5">
                  Crowdfunding means collecting small contributions from many kind-hearted people online to support a cause.
                </p>
                <p className="text-gray-500 text-base leading-relaxed mb-7">
                  On DonatePe, people across India come together to help patients in need. Whether it's for a child, elderly person, or urgent surgery — anyone can start a fundraiser.
                </p>
                <ul className="space-y-4">
                  {[
                    { title: "No repayment needed", desc: "Unlike loans, you don't have to return the money." },
                    { title: "Safe & Reliable", desc: "Your fundraiser is protected and monitored." },
                  ].map((item) => (
                    <li key={item.title} className="flex items-start gap-3">
                      <span className="w-6 h-6 min-w-[24px] rounded-full flex items-center justify-center mt-0.5" style={{ background: "#00B7B3" }}>
                        <CheckIcon />
                      </span>
                      <div>
                        <span className="font-bold text-gray-900 text-sm">{item.title}</span>
                        <span className="text-gray-500 text-sm"> — {item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

            </div>
          </div>
        </section>

        {/* ── SECTION 4: Why People Trust DonatePe ── */}
        <section className="py-20 px-5" aria-labelledby="trust-heading">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#00B7B3" }}>Trust & Safety</p>
              <h2
                id="trust-heading"
                className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-3"
                style={{ fontFamily: "Sora, sans-serif" }}
              >
                Why People Trust DonatePe?
              </h2>
            </div>
            <div className="grid md:grid-cols-5 gap-5">
              {[
                "Verified fundraisers to ensure genuine cases",
                "Secure payment gateways for safe donations",
                "Quick withdrawal process — access funds anytime",
                "Dedicated support team to guide you at every step",
                "Real people helping real patients — every day",
              ].map((item) => (
                <article
                  key={item}
                  className="bg-white rounded-2xl border border-gray-100 p-6 text-center transition-all duration-200 hover:-translate-y-1"
                  onMouseEnter={e => e.currentTarget.style.boxShadow = "0 8px 28px rgba(0,183,179,0.12)"}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                >
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "#E0F8F7", color: "#00B7B3" }}>
                    <CheckIcon />
                  </div>
                  <p className="text-sm text-gray-600 leading-relaxed font-medium">{item}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── SECTION 5: How Does Crowdfunding Work ── */}
        <section className="bg-gray-50 py-20 px-5" aria-labelledby="how-heading">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-xs font-bold tracking-widest uppercase mb-2" style={{ color: "#00B7B3" }}>Simple Steps</p>
              <h2
                id="how-heading"
                className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight mb-3"
                style={{ fontFamily: "Sora, sans-serif" }}
              >
                How Does Crowdfunding Work on DonatePe?
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-14 items-center">

              {/* Steps */}
              <div className="space-y-6">
                {[
                  {
                    num: "1",
                    title: "Start Your Fundraiser",
                    desc: "Create your fundraiser in minutes.",
                  },
                  {
                    num: "2",
                    title: "Share Your Campaign",
                    desc: "Share with friends, family, and social media.",
                  },
                  {
                    num: "3",
                    title: "Receive & Withdraw Funds",
                    desc: "Withdraw anytime without complications.",
                  },
                ].map((step) => (
                  <div key={step.num} className="flex items-start gap-5">
                    <div
                      className="w-14 h-14 min-w-[56px] rounded-2xl text-white font-extrabold text-xl flex items-center justify-center shadow-lg"
                      style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif", boxShadow: "0 6px 20px rgba(0,183,179,0.28)" }}
                    >
                      {step.num}
                    </div>
                    <div className="pt-1">
                      <h3 className="font-bold text-gray-900 mb-1" style={{ fontFamily: "Sora, sans-serif" }}>{step.title}</h3>
                      <p className="text-sm text-gray-500">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Image — Step-by-step process */}
              <img
                src="assets/group.png"
                alt="Step by step process of creating and sharing a fundraiser"
                className="w-full h-[400px] object-cover"
                loading="lazy"
              />

            </div>
          </div>
        </section>

        {/* ── SECTION 6: You're Not Alone + CTA ── */}
        <section
          className="py-20 px-5 text-center"
          style={{ background: "linear-gradient(135deg, #004D4B, #006B68)" }}
          aria-labelledby="cta-heading"
        >
          <div className="max-w-xl mx-auto">
            <h2
              id="cta-heading"
              className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4"
              style={{ fontFamily: "Sora, sans-serif" }}
            >
              You're Not Alone in This Journey
            </h2>
            <p className="text-lg mb-10" style={{ color: "rgba(255,255,255,0.75)" }}>
              In difficult times, people care. Thousands of donors are ready to help — all you need to do is take the first step.
            </p>
            <p
              className="text-xs font-bold tracking-widest uppercase mb-6"
              style={{ color: "#B2EEEC" }}
            >
              Take Action Now
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={handleStartFundraiser}
                className="text-white font-bold px-8 py-4 rounded-full text-sm transition-all hover:opacity-90 hover:-translate-y-0.5 transform"
                style={{ background: "#00B7B3", fontFamily: "Sora, sans-serif", boxShadow: "0 6px 20px rgba(0,183,179,0.35)" }}
                aria-label="Start a free medical fundraiser on DonatePe"
              >
                START A FREE FUNDRAISER
              </button>
              <button
                onClick={handleGetCallback}
                className="font-bold border-2 px-8 py-4 rounded-full text-sm transition-all hover:opacity-90"
                style={{ color: "#fff", borderColor: "rgba(255,255,255,0.4)", fontFamily: "Sora, sans-serif" }}
                aria-label="Get a callback from DonatePe support"
              >
                GET A CALLBACK
              </button>
            </div>
          </div>
        </section>

      </div>
    </>
  );
}